/**
 * User API Service
 *
 * API client for user-specific data including quota information and invitations.
 */

import { api } from "../lib/api";

// Types
export interface QuotaStatus {
  limit: number | null;
  used: number;
  remaining: number | null;
  percentUsed: number | null;
  exceeded: boolean;
  warning: boolean;
}

export interface UserQuota {
  userId: string;
  generation: QuotaStatus;
  runtime: QuotaStatus;
  resetAt: string;
}

export interface Invitation {
  id: string;
  code: string;
  invitedEmail: string | null;
  status: "pending" | "accepted" | "revoked";
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  acceptedBy: { displayName: string } | null;
  isExpired: boolean;
}

export interface InvitationDetails {
  code: string;
  invitedEmail: string | null;
  inviterName: string;
  personalMessage?: string | null;
  expiresAt: string;
}

export interface AppInviteDetails {
  app: { id: string; name: string; icon: string | null };
  inviter: { name: string; avatarUrl: string | null };
  personalMessage: string | null;
  expiresAt: string;
  userAlreadyHasAccess?: boolean;
}

export interface AppInvite {
  id: string;
  code: string;
  app: { id: string; name: string; icon: string | null };
  status: "pending" | "accepted" | "expired";
  createdAt: string;
  expiresAt: string;
  acceptedBy: { displayName: string } | null;
  isExpired: boolean;
}

export interface CreateInvitationResponse {
  invitation: {
    id: string;
    code: string;
    invitedEmail: string | null;
    status: string;
    createdAt: string;
    expiresAt: string;
  };
  invitesRemaining: number;
}

export interface AcceptInvitationResponse {
  success: boolean;
  user: { id: string; email: string; displayName: string };
  redirectTo?: string; // App ID to redirect to if there was a pending app invite
}

export const userApi = {
  /**
   * Get current user's token quota
   */
  getQuota: () => api.get<{ quota: UserQuota }>("/api/users/me/quota"),

  /**
   * Get current user's profile
   */
  getProfile: () =>
    api.get<{
      user: {
        id: string;
        email: string | null;
        displayName: string | null;
        avatarUrl: string | null;
        invitesRemaining: number;
      };
    }>("/api/users/me"),
};

export const inviteApi = {
  /**
   * Create a new invitation
   */
  create: (email?: string, personalMessage?: string) =>
    api.post<CreateInvitationResponse>("/api/invites", {
      ...(email && { email }),
      ...(personalMessage && { personalMessage }),
    }),

  /**
   * Get all invitations sent by current user
   */
  list: () =>
    api.get<{ invitations: Invitation[]; invitesRemaining: number }>(
      "/api/invites",
    ),

  /**
   * Get invitation details by code (public - for invite acceptance page)
   */
  getByCode: (code: string) =>
    api.get<{ invitation: InvitationDetails }>(`/api/invites/${code}`),

  /**
   * Accept an invitation and create an account
   */
  accept: (
    code: string,
    data: {
      email: string;
      password: string;
      displayName?: string;
      pendingAppInvite?: string;
    },
  ) => api.post<AcceptInvitationResponse>(`/api/invites/${code}/accept`, data),

  /**
   * Accept an invitation via OAuth (user already created by Google SSO)
   */
  acceptOAuth: (code: string, data?: { pendingAppInvite?: string }) =>
    api.post<AcceptInvitationResponse>(
      `/api/invites/${code}/accept-oauth`,
      data,
    ),

  /**
   * Revoke a pending invitation
   */
  revoke: (id: string) =>
    api.delete<{ success: boolean; invitesRemaining: number }>(
      `/api/invites/${id}`,
    ),
};

// ── New Unified Invite Link System ──

export interface InviteLink {
  id: string;
  code: string;
  appId: string | null;
  app: { id: string; name: string; icon: string | null } | null;
  maxUses: number;
  usesCount: number;
  effectiveRemaining: number;
  personalMessage: string | null;
  expiresAt: string;
  revokedAt: string | null;
  createdAt: string;
  uses: Array<{
    userId: string;
    displayName: string;
    avatarUrl: string | null;
    usedAt: string;
  }>;
}

export interface JoinPageDetails {
  inviter: { name: string; avatarUrl: string | null };
  app: { id: string; name: string; icon: string | null } | null;
  personalMessage: string | null;
  expiresAt: string;
  userAlreadyUsed?: boolean;
  userAlreadyHasAccess?: boolean;
  available: boolean;
}

export interface JoinAcceptResponse {
  success: boolean;
  appId?: string | null;
  newFriendship?: boolean;
  alreadyUsed?: boolean;
}

export interface JoinSignupResponse {
  success: boolean;
  appId?: string | null;
  newFriendship?: boolean;
  user: { id: string; email: string };
}

export const inviteLinkApi = {
  /** Create an invite link (free — no quota cost, limited by inviter's quota) */
  create: (data: {
    appId?: string;
    expirationDays?: number;
    personalMessage?: string;
  }) =>
    api.post<{ link: InviteLink; invitesRemaining: number }>(
      "/api/invite-links",
      data,
    ),

  /** List my invite links */
  list: () =>
    api.get<{ links: InviteLink[]; invitesRemaining: number }>(
      "/api/invite-links",
    ),

  /** Get link detail */
  get: (id: string) => api.get<{ link: InviteLink }>(`/api/invite-links/${id}`),

  /** Revoke a link */
  revoke: (id: string) =>
    api.delete<{ success: boolean }>(`/api/invite-links/${id}`),

  /** Get public join page details */
  getJoinDetails: (code: string) =>
    api.get<JoinPageDetails>(`/api/join/${code}`),

  /** Accept invite (existing logged-in user) */
  accept: (code: string) => api.post<JoinAcceptResponse>(`/api/join/${code}`),

  /** Sign up via invite (new user) */
  signup: (
    code: string,
    data: { email: string; password: string; displayName?: string },
  ) => api.post<JoinSignupResponse>(`/api/join/${code}/signup`, data),
};

// ── Legacy APIs (deprecated — use inviteLinkApi) ──

export const appInviteApi = {
  /**
   * Get app invite details by code (public endpoint)
   */
  getDetails: (code: string) =>
    api.get<AppInviteDetails>(`/api/apps/invite/${code}`),

  /**
   * Accept an app invite (logged-in users only)
   */
  accept: (code: string) =>
    api.post<{
      success: boolean;
      app: { id: string; name: string; icon: string | null } | null;
      newFriendship?: boolean;
      alreadyHasAccess?: boolean;
    }>(`/api/invite/${code}/accept`),

  /**
   * Sign up via app invite (creates account and joins app)
   */
  signUp: (
    code: string,
    data: { email: string; password: string; displayName?: string },
  ) =>
    api.post<{
      success: boolean;
      app: { id: string; name: string; icon: string | null } | null;
      newFriendship?: boolean;
      user: { id: string; email: string };
    }>(`/api/apps/invite/${code}/signup`, data),

  /**
   * Create an app invite link
   */
  createLink: (appId: string, personalMessage?: string, shareData?: boolean) =>
    api.post<{
      inviteCode: string;
      expiresAt: string;
      invitesRemaining: number;
    }>(`/api/apps/${appId}/share/link`, {
      ...(personalMessage && { personalMessage }),
      ...(shareData !== undefined && { shareData }),
    }),

  /**
   * List app invites created by current user
   */
  list: () => api.get<{ appInvites: AppInvite[] }>("/api/apps/invites"),

  /**
   * Revoke a pending app invite
   */
  revoke: (id: string) =>
    api.delete<{ success: boolean }>(`/api/apps/invites/${id}`),
};
